import React, { useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { isLocationEdgeType } from "../locationTypes";
import { hasGoogleMapsKey, loadGoogleMaps } from "../utils/googlePlaces";

function buildInfoContent(placeNode, inboundEdges, nodeById) {
  const container = document.createElement("div");
  container.style.maxWidth = "280px";
  container.style.fontFamily = "Arial, sans-serif";
  container.style.fontSize = "12px";

  const title = document.createElement("div");
  title.style.fontWeight = "700";
  title.style.marginBottom = "4px";
  title.textContent = placeNode.label || placeNode.props?.name || "Unnamed place";
  container.appendChild(title);

  const address = document.createElement("div");
  address.style.color = "#475569";
  address.style.marginBottom = "6px";
  address.textContent = placeNode.props?.formattedAddress || "No formatted address";
  container.appendChild(address);

  const summary = document.createElement("div");
  summary.style.marginBottom = "4px";
  summary.textContent = `${inboundEdges.length} linked node${inboundEdges.length === 1 ? "" : "s"}`;
  container.appendChild(summary);

  inboundEdges.slice(0, 12).forEach((edge) => {
    const row = document.createElement("div");
    row.style.display = "flex";
    row.style.gap = "6px";
    row.style.marginBottom = "2px";
    const relation = document.createElement("span");
    relation.style.fontWeight = "600";
    relation.textContent = edge.type;
    const subject = document.createElement("span");
    subject.textContent = nodeById.get(edge.from?.nodeId)?.label || edge.from?.nodeId || "Unknown";
    row.appendChild(relation);
    row.appendChild(subject);
    container.appendChild(row);
  });

  if (inboundEdges.length > 12) {
    const more = document.createElement("div");
    more.style.color = "#64748b";
    more.textContent = `+${inboundEdges.length - 12} more`;
    container.appendChild(more);
  }

  return container;
}

export default function LocationMapCanvas({
  nodes,
  edges,
  placeNodeIds,
  selectedNodeId,
  onSelectNode,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map());
  const infoWindowRef = useRef(null);

  const nodeById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);

  const placeNodes = useMemo(() => (
    nodes.filter((node) => {
      if (!placeNodeIds.has(node.id)) return false;
      return Number.isFinite(Number(node.props?.lat)) && Number.isFinite(Number(node.props?.lng));
    })
  ), [nodes, placeNodeIds]);

  const inboundByPlace = useMemo(() => {
    const map = new Map();
    edges.forEach((edge) => {
      if (!isLocationEdgeType(edge.type)) return;
      if (!placeNodeIds.has(edge.to?.nodeId)) return;
      if (!map.has(edge.to.nodeId)) map.set(edge.to.nodeId, []);
      map.get(edge.to.nodeId).push(edge);
    });
    return map;
  }, [edges, placeNodeIds]);

  useEffect(() => {
    let active = true;
    const ensureMap = async () => {
      if (!containerRef.current) return;
      const maps = await loadGoogleMaps();
      if (!active || !maps) return;
      if (!mapRef.current) {
        mapRef.current = new maps.Map(containerRef.current, {
          center: { lat: 39.5, lng: -98.35 },
          zoom: 3,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: "greedy",
        });
      }
      if (!infoWindowRef.current) {
        infoWindowRef.current = new maps.InfoWindow();
      }
    };
    ensureMap();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const syncMarkers = async () => {
      const maps = await loadGoogleMaps();
      if (!active || !maps || !mapRef.current) return;
      const map = mapRef.current;
      const markers = markersRef.current;
      const nextIds = new Set(placeNodes.map((node) => node.id));

      markers.forEach((marker, nodeId) => {
        if (!nextIds.has(nodeId)) {
          marker.setMap(null);
          markers.delete(nodeId);
        }
      });

      const bounds = new maps.LatLngBounds();
      placeNodes.forEach((placeNode) => {
        const position = { lat: Number(placeNode.props.lat), lng: Number(placeNode.props.lng) };
        bounds.extend(position);
        const inboundEdges = inboundByPlace.get(placeNode.id) || [];
        const isSelected = selectedNodeId === placeNode.id;
        let marker = markers.get(placeNode.id);
        if (!marker) {
          marker = new maps.Marker({
            map,
            position,
            title: placeNode.label || placeNode.props?.formattedAddress || placeNode.id,
            label: inboundEdges.length ? String(Math.min(inboundEdges.length, 99)) : undefined,
          });
          marker.addListener("click", () => {
            onSelectNode?.(placeNode.id);
            if (infoWindowRef.current) {
              infoWindowRef.current.setContent(buildInfoContent(placeNode, inboundEdges, nodeById));
              infoWindowRef.current.open({ map, anchor: marker });
            }
          });
          markers.set(placeNode.id, marker);
        } else {
          marker.setPosition(position);
          marker.setTitle(placeNode.label || placeNode.props?.formattedAddress || placeNode.id);
          marker.setLabel(inboundEdges.length ? String(Math.min(inboundEdges.length, 99)) : null);
        }
        marker.setIcon({
          path: maps.SymbolPath.CIRCLE,
          fillColor: isSelected ? "#dc2626" : "#2563eb",
          fillOpacity: 0.9,
          strokeColor: "#0f172a",
          strokeWeight: 1,
          scale: isSelected ? 9 : 7,
        });
      });

      if (placeNodes.length === 1) {
        map.setCenter({ lat: Number(placeNodes[0].props.lat), lng: Number(placeNodes[0].props.lng) });
        map.setZoom(12);
      } else if (placeNodes.length > 1) {
        map.fitBounds(bounds, 40);
      }
    };

    syncMarkers();
    return () => {
      active = false;
    };
  }, [placeNodes, inboundByPlace, nodeById, onSelectNode, selectedNodeId]);

  if (!hasGoogleMapsKey()) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Map mode requires `REACT_APP_PLACES_API_BROWSER_KEY` or `REACT_APP_GOOGLE_MAPS_API_KEY`.
        </Typography>
      </Box>
    );
  }

  return <Box ref={containerRef} sx={{ width: "100%", height: "82vh" }} />;
}
